import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, MessageCircle, Mail, MapPin, Clock, Instagram, Facebook, Music2 } from "lucide-react";
import { PageHero, Reveal } from "@/components/Reveal";
import { API, EMAIL, ADDRESS_1, ADDRESS_2, HOURS, SOCIALS, WA_KARINA, wa } from "@/lib/site";

const socialIcon = (name) =>
  name === "Instagram" ? <Instagram size={16} /> : name === "TikTok" ? <Music2 size={16} /> : <Facebook size={16} />;

const Contacto = () => {
  const [form, setForm] = useState({ nombre: "", contacto: "", motivo: "servicio", mensaje: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("done");
    } catch {
      setStatus("idle");
      setError("No pudimos enviar tu mensaje. Escríbeme directo por WhatsApp.");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-ink/25 focus:border-gold outline-none py-3 text-ink placeholder:text-ink/35 transition-colors duration-300 font-light";

  return (
    <div data-testid="contacto-page">
      <PageHero
        eyebrow="Contacto"
        lines={["Hablemos de", "tu próximo paso."]}
        sub="Escríbeme por el canal que prefieras — respondo personalmente cada mensaje."
      />

      <section className="bg-cream px-6 sm:px-12 lg:px-20 pb-20 lg:pb-32" data-testid="contacto-contenido">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <a
                href={wa(WA_KARINA, "Hola Karina, quiero información.")}
                target="_blank"
                rel="noreferrer"
                data-testid="contacto-whatsapp-button"
                className="flex items-center gap-4 rounded-full bg-ink text-cream px-7 py-5 hover:bg-gold hover:text-ink transition-colors duration-300 group"
              >
                <MessageCircle size={22} className="text-gold group-hover:text-ink transition-colors duration-300" />
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold group-hover:text-ink/70">WhatsApp directo</span>
                  <span className="font-serif text-xl">+34 691 536 518</span>
                </span>
              </a>
            </Reveal>

            {[
              { icon: <Mail size={18} className="text-gold" />, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, testid: "contacto-email" },
              { icon: <MapPin size={18} className="text-gold" />, label: "Dirección", value: `${ADDRESS_1} · ${ADDRESS_2}`, testid: "contacto-direccion" },
              { icon: <Clock size={18} className="text-gold" />, label: "Horario", value: HOURS.map(([d, h]) => `${d}: ${h}`).join(" · "), testid: "contacto-horario" },
            ].map((c) => (
              <Reveal key={c.label}>
                <div className="flex gap-4 border-b border-ink/10 pb-6" data-testid={c.testid}>
                  <span className="mt-1 shrink-0">{c.icon}</span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-1">{c.label}</span>
                    {c.href ? (
                      <a href={c.href} className="text-ink/80 hover:text-gold transition-colors duration-300 break-all">{c.value}</a>
                    ) : (
                      <span className="text-ink/80 leading-relaxed">{c.value}</span>
                    )}
                  </span>
                </div>
              </Reveal>
            ))}

            <Reveal>
              <div className="flex gap-3 pt-2">
                {SOCIALS.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.name}
                    data-testid={`contacto-social-${s.name.toLowerCase()}`}
                    className="w-11 h-11 rounded-full border border-ink/25 flex items-center justify-center text-ink hover:bg-ink hover:text-gold hover:border-ink transition-colors duration-300"
                  >
                    {socialIcon(s.name)}
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <AnimatePresence mode="wait">
              {status === "done" ? (
                <motion.div
                  key="ok"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  className="border border-gold/50 bg-beige p-10 lg:p-14"
                  data-testid="contacto-success"
                >
                  <span className="w-14 h-14 rounded-full bg-ink text-gold flex items-center justify-center">
                    <Check size={26} />
                  </span>
                  <h2 className="font-serif text-3xl mt-8">Mensaje recibido.</h2>
                  <p className="text-ink/70 mt-4 leading-relaxed">
                    Gracias por escribir. Te responderé personalmente lo antes posible — si es urgente,
                    escríbeme directo por WhatsApp.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  className="border border-sand bg-beige p-8 sm:p-10 lg:p-14 space-y-8"
                  data-testid="contacto-form"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Formulario de contacto</p>
                  <div className="grid sm:grid-cols-2 gap-8">
                    <input
                      required
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      placeholder="Nombre"
                      data-testid="contacto-nombre-input"
                      className={inputCls}
                    />
                    <input
                      required
                      value={form.contacto}
                      onChange={(e) => setForm({ ...form, contacto: e.target.value })}
                      placeholder="Email o teléfono"
                      data-testid="contacto-contacto-input"
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-4">Es sobre</p>
                    <div className="flex gap-3">
                      {[
                        ["servicio", "Un servicio"],
                        ["formacion", "Formación"],
                      ].map(([id, label]) => (
                        <button
                          type="button"
                          key={id}
                          onClick={() => setForm({ ...form, motivo: id })}
                          data-testid={`contacto-motivo-${id}`}
                          className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                            form.motivo === id
                              ? "border-gold bg-ink text-gold"
                              : "border-ink/25 text-ink/60 hover:border-gold"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <textarea
                    required
                    rows={4}
                    value={form.mensaje}
                    onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                    placeholder="Tu mensaje"
                    data-testid="contacto-mensaje-input"
                    className={`${inputCls} resize-none`}
                  />

                  {error && <p className="text-sm text-gold" data-testid="contacto-error">{error}</p>}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="contacto-submit-button"
                    className="inline-flex items-center gap-3 rounded-full bg-ink text-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60"
                  >
                    {status === "loading" && <Loader2 size={15} className="animate-spin" />}
                    Enviar mensaje
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
